import React, { useState, useEffect, useRef, useCallback } from 'react';
import Model, { helper } from '@tripian/model';

import './SearchPlace.scss';
import { TextField, PoiRefCard, Button, PreLoading, Checkbox } from '@tripian/react';

interface ISearchPlace {
  // for input
  poiCategoryIds: number[];
  recommendedPois: Model.Poi[];
  search: (query: string, searchPoiCategories?: number[], page?: number) => Promise<Model.DataPayload<Model.Poi[]>>;

  // for actions
  focus: (poi: Model.Poi) => void;
  cardButonOnClick: (poi: Model.Poi) => void;

  // helper
  isCurrentDayStep: (poiId: number) => boolean;
  getDayIndexes: (poiId: number) => number[];
}

const SearchPlace: React.FC<ISearchPlace> = ({ poiCategoryIds, recommendedPois, search, focus, cardButonOnClick, isCurrentDayStep, getDayIndexes }) => {
  // Query search
  const [query, setQuery] = useState<string>('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryPageIndex, setQueryPageIndex] = useState(1);
  const [queryResults, setQueryResults] = useState<Model.Poi[] | undefined>(undefined);
  const [queryFinished, setQueryFinished] = useState(false);

  // Default category pois
  const [categoryInit, setCategoryInit] = useState(true);
  const [categoryInitTimer, setCategoryInitTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [categoryPageIndex, setCategoryPageIndex] = useState(1);
  const [categoryResults, setCategoryResults] = useState<Model.Poi[] | undefined>();
  const [categoryFinished, setCategoryFinished] = useState(false);

  // Recommended place
  const [recommendedShow, setRecommendedShow] = useState(true);

  // Load more
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const timerRef = useRef<number | undefined>(undefined);

  const fetchFirstCategoryPage = useCallback(() => {
    if (categoryInit) {
      // console.log('searchAttraction called');
      search('', poiCategoryIds, categoryPageIndex).then((newPois) => {
        // console.log('searchAttraction called result');
        setCategoryResults((prevCategoryResult) => {
          if (prevCategoryResult) return helper.removeDuplicateValuesById<Model.Poi>(prevCategoryResult.concat(newPois.data));
          return newPois.data;
        });
        if (newPois.pagination?.links.next === null) setCategoryFinished(true);
      });
      setCategoryInit(false);
    }
  }, [categoryInit, categoryPageIndex, poiCategoryIds, search]);

  /* page 1 default category pois */
  useEffect(() => {
    if (categoryInit && categoryInitTimer === undefined) {
      // console.log('SearchPlace initial useEffect');
      const timer = setTimeout(fetchFirstCategoryPage, 3000);
      setCategoryInitTimer(timer);
    }
    return () => {
      // console.log('UNMOUNT! SearchPlace return useEffect');
      if (categoryInitTimer) clearTimeout(categoryInitTimer);
    };
  }, [categoryInit, categoryInitTimer, fetchFirstCategoryPage]);

  /* useEffect(() => {
    console.log('MOUNT SearchPlace');

    return () => {
      console.log('UNMOUNT SearchPlace');
    };
  }, []); */

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  /* query search */
  useEffect(() => {
    let didCancel = false;

    if (query.length > 2) {
      if (timerRef.current) window.clearTimeout(timerRef.current);

      const fetchPois = async () => {
        setQueryLoading(true);
        setQueryResults(undefined);
        setQueryPageIndex(1);
        setQueryFinished(false);
        const searchPoisResult = await search(query, poiCategoryIds, 1);

        if (!didCancel) {
          setQueryResults(searchPoisResult.data);
          if (searchPoisResult.pagination?.links.next === null) setQueryFinished(true);
          setQueryLoading(false);
        }
      };

      timerRef.current = window.setTimeout(() => {
        fetchPois();
      }, 400);
    } else {
      setQueryLoading(false);
      if (query.length === 0) setQueryResults(undefined);
    }

    return () => {
      clearTimeout(timerRef.current);
      didCancel = true;
    };
  }, [poiCategoryIds, query, search]);

  // page for query paging
  const queryLoadMore = async () => {
    if (query.length > 2 && queryResults && queryResults.length > 19) {
      setLoadMoreLoading(true);
      // page index
      const newPageIndex = queryPageIndex + 1;
      setQueryPageIndex(newPageIndex);
      const newQueryResults = await search(query, poiCategoryIds, newPageIndex);
      // set results
      setQueryResults(helper.removeDuplicateValuesById<Model.Poi>(queryResults.concat(newQueryResults.data)));
      setLoadMoreLoading(false);
      if (newQueryResults.pagination?.links.next === null) setQueryFinished(true);
    } else {
      // eslint-disable-next-line no-console
      console.error('q < 3 ama neye load more tıklıyon, saklamalıyız bu butonu!');
    }
  };

  // page for category paging
  const categoryLoadMore = async () => {
    setLoadMoreLoading(true);
    // page index
    const newPageIndex = categoryPageIndex + 1;
    setCategoryPageIndex(newPageIndex);
    const newCategoryResults = await search('', poiCategoryIds, newPageIndex);

    // set results
    setCategoryResults((prevCategoryResult) => {
      if (prevCategoryResult) return helper.removeDuplicateValuesById<Model.Poi>(prevCategoryResult.concat(newCategoryResults.data));
      return newCategoryResults.data;
    });
    if (newCategoryResults.pagination?.links.next === null) setCategoryFinished(true);
    setLoadMoreLoading(false);
  };

  const loadMore = () => {
    if (queryLoading) return null;

    if (queryResults) {
      if (queryResults.length > 19)
        if (!queryFinished)
          return (
            <div className="center m5">
              <Button color="primary" text="Load More" onClick={queryLoadMore} />
            </div>
          );
      return null;
    }

    if (!categoryFinished)
      return (
        <div className="center m5">
          <Button color="primary" text="Load More" onClick={categoryLoadMore} />
        </div>
      );

    return null;
  };

  const renderList = (poiList?: Model.Poi[]) =>
    poiList?.map((poiResult) => {
      const dayIndexes = getDayIndexes(poiResult.id);
      const isRecommendation = !dayIndexes.includes(-2);
      let dayNumbers: number[] | undefined;
      if (isRecommendation && !dayIndexes.includes(-1)) {
        dayNumbers = dayIndexes.map((dayIndex) => dayIndex + 1);
      }

      return (
        <PoiRefCard
          key={`search-result-poi-${poiResult.id}`}
          poi={poiResult}
          buttonType={1}
          poiCardClicked={focus}
          inStep={isCurrentDayStep(poiResult.id)}
          isRecommendation={isRecommendation}
          dayNumbers={dayNumbers}
          addRemoveAlternativePoi={cardButonOnClick}
        />
      );
    });

  const renderResult = () => {
    /* This PreLoading means: Searching.... wait. */
    if (queryLoading)
      return (
        <div style={{ height: '4rem' }}>
          <PreLoading size="large" />
        </div>
      );

    if (queryResults) {
      if (queryResults.length === 0) return <span>No results found.</span>;
      return renderList(queryResults);
    }

    if (recommendedShow) return renderList(helper.removeDuplicateValuesById<Model.Poi>(recommendedPois.concat(categoryResults || [])));

    return renderList(helper.removeDuplicateValuesById<Model.Poi>(categoryResults || []));

    // return <span>initial category loading</span>;
  };

  return (
    <div className="search-place">
      <div className="mt3">
        <TextField
          name={`search-textfield-${poiCategoryIds.join('-')}`}
          placeholder="Search"
          onChange={onChange}
          value={query}
          size="small"
          type="primary"
          // className="search-place-icon"
        />

        <Checkbox
          domId="SearchPlaceRecommendations"
          text="Recommendations"
          checked={recommendedShow}
          onChange={() => {
            setRecommendedShow(!recommendedShow);
          }}
        />
      </div>
      {renderResult()}
      {/* This PreLoading means: Load more loading.. wait. */}
      {loadMoreLoading ? (
        <div style={{ height: '4rem' }}>
          <PreLoading />
        </div>
      ) : (
        loadMore()
      )}
    </div>
  );
};

export default SearchPlace;
